import React, { lazy, ComponentType } from "react";

// Lightweight inline spinner used by every route-level Suspense fallback and
// by ProtectedRoute's auth-loading state, so all loading transitions share one
// visual instead of cascading mismatched skeletons.
export const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Lazy import with retry + delay (no auto-reload to avoid loops)
function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  const Component = lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      // Wait briefly then retry once (allows Vite to stabilize after HMR)
      await new Promise(r => setTimeout(r, 1500));
      return await factory();
    }
  });
  return { Component, preload: factory };
}

// Main pages - lazy loaded
export const Home = lazyWithRetry(() => import("@/pages/Home"));
export const Index = lazyWithRetry(() => import("@/pages/Index"));
export const Library = lazyWithRetry(() => import("@/pages/Library"));
export const Profile = lazyWithRetry(() => import("@/pages/Profile"));
export const Chat = lazyWithRetry(() => import("@/pages/Chat"));
export const Events = lazyWithRetry(() => import("@/pages/Events"));
export const Settings = lazyWithRetry(() => import("@/pages/Settings"));

// Detail pages
export const Recipe = lazyWithRetry(() => import("@/pages/Recipe"));
export const CourseView = lazyWithRetry(() => import("@/pages/CourseView"));
export const LessonView = lazyWithRetry(() => import("@/pages/LessonView"));
export const ChallengeVideo = lazyWithRetry(() => import("@/pages/ChallengeVideo"));
export const EventDetails = lazyWithRetry(() => import("@/pages/EventDetails"));

// Feature pages
export const WaterTracking = lazyWithRetry(() => import("@/pages/WaterTracking"));
export const Fasting = lazyWithRetry(() => import("@/pages/Fasting"));
export const Steps = lazyWithRetry(() => import("@/pages/Steps"));
export const Subscribe = lazyWithRetry(() => import("@/pages/Subscribe"));
// "הנתונים שלך" and one metric's history. Auth-gated only, never
// subscription-gated (D2) — the lapsed member and the store reviewer both reach
// these from the paywall link.
export const ProgressPage = lazyWithRetry(() => import("@/pages/ProgressPage"));
export const MetricHistoryPage = lazyWithRetry(() => import("@/pages/MetricHistoryPage"));

// Admin pages
export const AnalyticsPage = lazyWithRetry(() => import("@/pages/Analytics"));
export const AudienceManagement = lazyWithRetry(() => import("@/pages/admin/AudienceManagement"));
export const PostsManagement = lazyWithRetry(() => import("@/pages/admin/PostsManagement"));
export const Moderation = lazyWithRetry(() => import("@/pages/Moderation"));
export const OnboardingManagement = lazyWithRetry(() => import("@/pages/admin/OnboardingManagement"));
export const FunnelManagement = lazyWithRetry(() => import("@/pages/admin/FunnelManagement"));
export const CourseProductsManagement = lazyWithRetry(() => import("@/pages/admin/CourseProductsManagement"));
export const ChallengeManagement = lazyWithRetry(() => import("@/pages/admin/ChallengeManagement"));
export const ChallengeEditor = lazyWithRetry(() => import("@/pages/admin/ChallengeEditor"));
export const CourseEditorPage = lazyWithRetry(() => import("@/pages/admin/CourseEditor"));
export const FastingNotificationsManagement = lazyWithRetry(() => import("@/pages/admin/FastingNotificationsManagement"));
export const NotificationsManagement = lazyWithRetry(() => import("@/pages/admin/NotificationsManagement"));
export const SubscriptionBannersManager = lazyWithRetry(() => import("@/pages/admin/SubscriptionBannersManager"));
export const AdminEntryPopups = lazyWithRetry(() => import("@/pages/admin/AdminEntryPopups"));

// Auth & Onboarding - lazy loaded
export const LoginPage = lazyWithRetry(() => import("@/pages/Login"));
export const OnboardingStepPage = lazyWithRetry(() => import("@/pages/onboarding/OnboardingStep"));
// Acquisition funnel — public, pre-account. Gated by funnel_enabled_<platform>.
export const FunnelStepPage = lazyWithRetry(() => import("@/pages/funnel/FunnelStep"));
export const FunnelDiagnosisPage = lazyWithRetry(() => import("@/pages/funnel/FunnelDiagnosis"));
export const FunnelPaywallPage = lazyWithRetry(() => import("@/pages/funnel/FunnelPaywall"));
export const FunnelSignupPage = lazyWithRetry(() => import("@/pages/funnel/FunnelSignup"));
export const FunnelCompletePage = lazyWithRetry(() => import("@/pages/funnel/FunnelComplete"));
export const PersonalPlanPage = lazyWithRetry(() => import("@/pages/PersonalPlan"));
export const NotFoundPage = lazyWithRetry(() => import("@/pages/NotFound"));
export const PrivacyPolicy = lazyWithRetry(() => import("@/pages/PrivacyPolicy"));
export const TermsOfUse = lazyWithRetry(() => import("@/pages/TermsOfUse"));
export const Support = lazyWithRetry(() => import("@/pages/Support"));
export const DeleteAccount = lazyWithRetry(() => import("@/pages/DeleteAccount"));
export const OAuthConsent = lazyWithRetry(() => import("@/pages/OAuthConsent"));

// Referral — the member's share screen and the friend's path. Registered
// unconditionally (spec R5): each page handles the kill switch itself, and
// PendingReferralRedirect is inert while it is off.
export const ReferFriends = lazyWithRetry(() => import("@/pages/ReferFriends"));
export const InviteLanding = lazyWithRetry(() => import("@/pages/InviteLanding"));
export const RedeemReferral = lazyWithRetry(() => import("@/pages/RedeemReferral"));

// Preload main pages for faster navigation
export function preloadMainPages() {
  Home.preload();
  Library.preload();
  Profile.preload();
  Chat.preload();
  Events.preload();
}
